import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

interface RangeParams {
  start: string; // YYYY-MM-01
  end: string;   // YYYY-MM-01
  include_mailers?: boolean;
  /** CSM filter token from /csm-options (e.g. "owner:123", "partner:456"). */
  csm?: string | null;
}

function toQs(p: RangeParams): string {
  const qs = new URLSearchParams({ start: p.start, end: p.end });
  if (p.include_mailers) qs.set("include_mailers", "true");
  if (p.csm) qs.set("csm", p.csm);
  return qs.toString();
}

// ─── CSM filter options ─────────────────────────────────────────────

export interface CsmFilterOption {
  /** Token passed back as ?csm= (e.g. "owner:123", "user:<uuid>", "partner:456"). */
  value: string;
  label: string;
  kind: "owner" | "user" | "partner";
  account_count: number;
}

/** CSMs (and managing partners) accounts are assigned to, from the renewals
 *  CSM data. Backs the Customer Tiers CSM filter. */
export function useCsmOptions() {
  return useQuery({
    queryKey: ["customer-tiers", "csm-options"],
    queryFn: () => apiFetch<{ options: CsmFilterOption[] }>("/customer-tiers/csm-options"),
    staleTime: 60_000,
  });
}

export interface MovementResponse {
  rows: Array<{
    counterparty_id: string | number;
    counterparty_name: string;
    first_period: string;
    last_period: string;
    tier_at_start: number | null;
    tier_at_end: number | null;
    mrr_at_start: number;
    mrr_at_end: number;
    movement: "UPGRADED" | "DOWNGRADED" | "STABLE";
  }>;
  kpis: {
    in_window: number;
    upgraded: number;
    stable: number;
    downgraded: number;
    upgraded_mrr_delta: number;
    stable_mrr_delta: number;
    downgraded_mrr_delta: number;
  };
  matrix: Record<string, number>;
  meta: { start: string; end: string; include_mailers: boolean };
}

export function useTierMovement(params: RangeParams) {
  return useQuery({
    queryKey: ["customer-tiers", "movement", params],
    queryFn: () => apiFetch<MovementResponse>(`/customer-tiers/movement?${toQs(params)}`),
    enabled: !!(params.start && params.end),
  });
}

export interface PeakChurnResponse {
  summary: Array<{
    peak_tier: number;
    reached: number;
    churned: number;
    peak_mrr_lost: number;
    churn_rate_pct: number;
  }>;
  churned: Array<Record<string, unknown>>;
  all_rows: Array<Record<string, unknown>>;
  meta: Record<string, unknown>;
}

export function usePeakChurn(params: RangeParams) {
  return useQuery({
    queryKey: ["customer-tiers", "peak-churn", params],
    queryFn: () => apiFetch<PeakChurnResponse>(`/customer-tiers/peak-churn?${toQs(params)}`),
    enabled: !!(params.start && params.end),
  });
}

export interface LeaderboardResponse {
  rows: Array<{
    counterparty_id: string | number;
    counterparty_name: string;
    beginning_arr: number;
    ending_arr: number;
    arr_delta: number;
    arr_delta_pct: number | null;
    beginning_rank: number | null;
    ending_rank: number | null;
    rank_change: number | null;
    /** Ranks within the filtered CSM book — null unless a CSM filter is active. */
    beginning_rank_in_book: number | null;
    ending_rank_in_book: number | null;
    rank_change_in_book: number | null;
    lifecycle_status: "NEW" | "CHURNED" | "CONTINUING" | "MERGED";
  }>;
  meta: Record<string, unknown> & { csm?: string | null };
}

export function useArrLeaderboard(params: RangeParams) {
  return useQuery({
    queryKey: ["customer-tiers", "arr-leaderboard", params],
    queryFn: () => apiFetch<LeaderboardResponse>(`/customer-tiers/arr-leaderboard?${toQs(params)}`),
    enabled: !!(params.start && params.end),
  });
}
